import { useNavigate, Routes, Route, useSearchParams } from "react-router";
import { useEffect, useState, useRef, createContext } from "react";
import { setupInterceptors } from "./utilities/axiosSetup";
import { Alert } from "antd";
import Dashboard from "./Dashboard";
import CallsReport from "./pages/reports/CallsReport";
import AuthContainerPage from "./pages/auth/AuthContainerPage";
import LoginWrapper from "./components/auth/LoginWrapper";
import OTPWrapper from "./components/auth/OTPWrapper";
import SetupMfaWrapper from "./components/auth/SetupMfaWrapper";
import NewPasswordWrapper from "./components/auth/NewPasswordWrapper";
import ForgotPasswordWrapper from "./components/auth/ForgotPasswordWrapper";
import ResetPasswordWrapper from "./components/auth/ResetPasswordWrapper";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/routing/ProtectedRoute";
import PublicRoute from "./components/routing/PublicRoute";
import RoleBasedRedirect from "./components/routing/RoleBasedRedirect";
import Unauthorized from "./pages/Unauthorized";
import AdminDashboard from "./pages/admin/Dashboard";
import CustomerDashboard from "./pages/customer/Dashboard";
import CustomerPage from "./pages/admin/customer/CustomerPage";
import * as Sentry from "@sentry/react";
import DummyFirstPage from "./pages/auth/DummyFirstPage";
import AddCustomerForm from "./pages/admin/customer/AddCustomerForm";
import EditCustomerForm from "./pages/admin/customer/EditCustomerForm";
import AddInterpreterTier4Form from "./pages/interpreter/AddInterpreterTier4Form";
import EditInterpreterForm from "./pages/interpreter/EditInterpreterForm";
import AgentView from "./components/agent/AgentView";
import InterpreterView from "./components/interpreter/InterpreterView";
import InterpretersList from "./pages/interpreter/InterpretersList";
import { list_all_languages } from "./api/GeneralApi";
import InterpreterInfo from "./pages/interpreter/InterpreterInfo";
import AgentsList from "./pages/agent/AgentsList";

if (import.meta.env.VITE_ENV_MODE != "DEV") {
  Sentry.init({
    dsn: "https://3875937586deacd9f66bc7eab2c241bb@o4508013519634432.ingest.de.sentry.io/4509332145963088",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    enableLogs: true,
  });
}

export const GlobalContext = createContext();
function App() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [userId, setUserId] = useState("");
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const { login: authLogin } = useAuth();
  const timerRef = useRef(null);
  const [languages, setLanguages] = useState([]);
  const getData = async () => {
    let response = await list_all_languages();
    setLanguages(response.data);
  };

  useEffect(() => {
    getData();
    // Setup axios interceptors with navigation
    setupInterceptors(navigate);
  }, [navigate]);
  // Handle toast visibility and auto-dismissal
  useEffect(() => {
    if (toastOpen) {
      // Clear any existing timers
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set timer to hide the alert after 3 seconds
      timerRef.current = setTimeout(() => {
        setToastOpen(false);
      }, 5000);
    }

    // Cleanup timer on component unmount or when toast state changes
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [toastOpen]);

  return (
    <GlobalContext.Provider
      value={{
        setToastOpen: setToastOpen,
        setToastMessage: setToastMessage,
        setToastSeverity: setToastSeverity,
        languages: languages,
      }}
    >
      <div className="fixed z-50 bottom-4 left-1/2 transform -translate-x-1/2 transition-all duration-300 ease-in-out pointer-events-none">
        <Alert
          className={`shadow-lg ${
            toastOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          message={toastMessage}
          type={toastSeverity}
          showIcon
        />
      </div>
      <Routes>
        {/* Public routes */}
        <Route
          path="/dummy"
          element={
            <PublicRoute>
              <AuthContainerPage>
                <DummyFirstPage />
              </AuthContainerPage>
            </PublicRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <AuthContainerPage>
                <LoginWrapper />
              </AuthContainerPage>
            </PublicRoute>
          }
        />
        <Route
          path="/otp"
          element={
            <PublicRoute>
              <AuthContainerPage>
                <OTPWrapper />
              </AuthContainerPage>
            </PublicRoute>
          }
        />
        <Route
          path="/mfa-setup"
          element={
            <PublicRoute>
              <AuthContainerPage>
                <SetupMfaWrapper />
              </AuthContainerPage>
            </PublicRoute>
          }
        />
        <Route
          path="/new-password"
          element={
            <PublicRoute>
              <AuthContainerPage>
                <NewPasswordWrapper />
              </AuthContainerPage>
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <AuthContainerPage>
                <ForgotPasswordWrapper />
              </AuthContainerPage>
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <AuthContainerPage>
                <ResetPasswordWrapper />
              </AuthContainerPage>
            </PublicRoute>
          }
        />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Root redirect based on role */}
        <Route path="/" element={<RoleBasedRedirect />} />

        {/* Admin routes */}
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Dashboard>
                <AdminDashboard />
              </Dashboard>
            </ProtectedRoute>
          }
        />

        {/* Customer routes */}
        <Route
          path="/customer"
          element={
            <ProtectedRoute allowedRoles={["customer"]}>
              <Dashboard>
                <CustomerDashboard />
              </Dashboard>
            </ProtectedRoute>
          }
        />

        {/* Agent routes */}
        <Route
          path="/agent"
          element={
            <ProtectedRoute
              allowedRoles={[
                "agent",
                "admin",
                "backofficesupport",
                "supervisor",
              ]}
            >
              <Dashboard>
                <AgentView />
              </Dashboard>
            </ProtectedRoute>
          }
        />

        {/* Interpreter routes */}
        <Route
          path="/interpreter"
          element={
            <ProtectedRoute allowedRoles={["interpreter"]}>
              <Dashboard>
                <InterpreterView />
              </Dashboard>
            </ProtectedRoute>
          }
        />

        {/* Customer Management - Admin only */}
        <Route
          path="/admin/customers"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Dashboard>
                <CustomerPage />
              </Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/customers/add"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Dashboard>
                <AddCustomerForm />
              </Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/customers/edit/:id"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Dashboard>
                <EditCustomerForm />
              </Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/interpreters"
          element={
            <ProtectedRoute allowedRoles={["admin", "supervisor"]}>
              <Dashboard>
                <InterpretersList />
              </Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Dashboard></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agents"
          element={
            <ProtectedRoute allowedRoles={["admin", "supervisor"]}>
              <Dashboard>
                <AgentsList />
              </Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/interpreters/add-tier4"
          element={
            <ProtectedRoute allowedRoles={["admin", "supervisor"]}>
              <Dashboard>
                <AddInterpreterTier4Form />
              </Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/interpreters/edit/:id"
          element={
            <ProtectedRoute allowedRoles={["admin", "supervisor"]}>
              <Dashboard>
                <EditInterpreterForm />
              </Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/interpreters/view/:id"
          element={
            <ProtectedRoute allowedRoles={["admin", "supervisor"]}>
              <Dashboard>
                <InterpreterInfo />
              </Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Reports - accessible by multiple roles */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["admin", "supervisor"]}>
              <Dashboard>
                <CallsReport />
              </Dashboard>
            </ProtectedRoute>
          }
        />
      </Routes>
    </GlobalContext.Provider>
  );
}

export default App;
